/** This file contains utility functions and React components for parsing and rendering structured generative AI output (`AiDocument`). */
import { type JSX } from 'react';

import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { Text } from 'Components/Text/Text';
import { toTitleCase } from 'Helpers/StringUtils';
import { AiDocument, AiDocumentContent, AiDocumentSection } from 'Models/ArtificialIntelligence';

import styles from './AiDocumentRenderer.module.css';

/** Renders a list of content items (text, bullets, numbers, fields). */
const ContentListRenderer = ({ content }: { content: AiDocumentContent[] }): JSX.Element => (
    <div className={styles.content}>
        {content.map((item, idx) => (
            <div key={idx}>
                {item.text_string && <Text noStyles>{item.text_string}</Text>}
                {item.bullet_list && item.bullet_list.map((bullet, bulletIdx) => <Text key={bulletIdx}>{bullet.text_string}</Text>)}
                {item.number_list && item.number_list.map((num, numIdx) => <Text key={numIdx}>{`${numIdx + 1}. ${num.text_string}`}</Text>)}
                {item.field_list &&
                    item.field_list.map((field, fieldIdx) => (
                        <div key={fieldIdx}>
                            <FieldLabel fieldName={toTitleCase(field.field_name)} />
                            <Text>{field.field_value}</Text>
                        </div>
                    ))}
            </div>
        ))}
    </div>
);

/** Renders a section with its content and subsections. */
const SectionRenderer = ({ section }: { section: AiDocumentSection }): JSX.Element => (
    <div className={styles.section}>
        {section.content_list && <ContentListRenderer content={section.content_list} />}
        {section.subsection_list &&
            section.subsection_list.map((subsection, idx) => (
                <div key={idx}>
                    <Text variant="Header3">{subsection.subsection_header_string}</Text>
                    {subsection.content_list && <ContentListRenderer content={subsection.content_list} />}
                </div>
            ))}
    </div>
);

/** Renders structured generative AI output for displaying within the body of a page. Each AiDocument `section` corresponds to a `PageLayout...` body section. */
export const renderAiDocument = (aiDocument: AiDocument): { title: string; content: JSX.Element }[] => {
    const sections: { title: string; content: JSX.Element }[] = [];

    if (aiDocument.section_list) {
        for (const section of aiDocument.section_list) {
            sections.push({
                title: section.section_header_string,
                content: <SectionRenderer section={section} />,
            });
        }
    }

    return sections;
};
